import {
  useRef,
  type MouseEvent as ReactMouseEvent,
  type TouchEvent as ReactTouchEvent,
} from 'react';

import { isOwnEvent } from '../components/interactions-overlay/utils/is-own-event.js';
import { useTooltip } from '../components/overlay-tooltip/contexts/TooltipContext.js';
import type { Data } from '../components/overlay-tooltip/types.js';
import type { EventCoords } from '../components/overlay-tooltip/useTooltipActions.js';
import { useTooltipActions } from '../components/overlay-tooltip/useTooltipActions.js';
import { isTapGesture } from '../components/overlay-tooltip/utils/tap-gesture.js';

export type Options = {
  getTooltipData: (coords: EventCoords) => Data | undefined;
};

export const useOverlayEventHandlers = (options: Options) => {
  const tooltipStore = useTooltip();
  const { hover, pin, unpin, outOfBounds } = useTooltipActions();
  const { getTooltipData } = options;
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);

  const onPointerMove = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!isOwnEvent(event) || tooltipStore.getState().pinned) {
      return;
    }

    const tooltipData = getTooltipData(event.nativeEvent);
    if (tooltipData) {
      hover(event.nativeEvent, tooltipData);
    } else {
      outOfBounds();
    }
  };

  const onPointerLeave = () => {
    outOfBounds();
  };

  const onTouchStart = (event: ReactTouchEvent<HTMLDivElement>) => {
    if (!isOwnEvent(event)) {
      touchStartPosRef.current = null;
      return;
    }
    const touch = event.changedTouches[0];
    if (!touch) {
      return;
    }
    touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
  };

  // Handles tooltip pin/unpin on touch. Fires before pointerleave/outOfBounds so the
  // pinned state survives the SET_OUT_BOUNDS action (which no-ops when pinned=true).
  // Calling preventDefault() suppresses the subsequent synthetic click event so the
  // desktop onClick path does not double-fire. Movement tracking prevents a scroll
  // gesture that starts and ends on the overlay from incorrectly unpinning the tooltip.
  const onTouchEnd = (event: ReactTouchEvent<HTMLDivElement>) => {
    if (!isOwnEvent(event)) {
      return;
    }

    const touch = event.changedTouches[0];
    if (!touch) {
      return;
    }

    const start = touchStartPosRef.current;
    touchStartPosRef.current = null;

    if (start !== null && !isTapGesture(start, touch)) {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    const tooltipData = getTooltipData({
      clientX: touch.clientX,
      clientY: touch.clientY,
    });
    const isPinned = tooltipStore.getState().pinned;

    if (isPinned) {
      unpin();
      if (tooltipData) {
        hover(touch, tooltipData);
      }
    } else if (tooltipData) {
      pin(touch, tooltipData);
    }
  };

  const onClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!isOwnEvent(event)) {
      return;
    }

    const tooltipData = getTooltipData(event.nativeEvent);
    const isPinned = tooltipStore.getState().pinned;

    if (isPinned) {
      if (tooltipData) {
        unpin();
        hover(event.nativeEvent, tooltipData);
      }
    } else if (tooltipData) {
      pin(event.nativeEvent, tooltipData);
    }
  };

  return {
    onPointerMove,
    onPointerLeave,
    onTouchStart,
    onTouchEnd,
    onClick,
  };
};
